import { Button, Stack, Typography } from "@mui/joy";
import { useCallback, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RISK_DETAIL_ENDPOINT,
  RISK_DOMAIN,
  riskApi,
  RiskDetailType,
} from "api/riskApi";
import TaskLink from "./TaskLink";
import { Status } from "./workflow/CoachWorkflow";
import { PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT } from "api/patientApi";
import CustomModal from "components/common/CustomModal";

function Risk({
  taskKeyList,
  thoughtRecordKey,
  isReportedToMedicalStaff,
}: {
  taskKeyList: string[];
  thoughtRecordKey?: string;
  isReportedToMedicalStaff: boolean;
}) {
  const queryClient = useQueryClient();
  const [patientState, setPatientState] = useRecoilState(patientAtom);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);

  const handleAfterReported = () => {
    setIsConsentModalOpen(false);

    setPatientState((patientState) =>
      patientState
        ? { ...patientState, isRiskCheckRequired: false }
        : patientState
    );

    queryClient.invalidateQueries([
      RISK_DOMAIN,
      RISK_DETAIL_ENDPOINT,
      patientState?.patientId,
    ]);

    queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
  };

  const reportQueryFn = useCallback(
    () => riskApi.report(patientState?.patientId!!),
    [patientState?.patientId]
  );

  const { mutate: report, isLoading } = useMutation(reportQueryFn, {
    onSuccess: () => {},
  });

  const skipQueryFn = useCallback(
    () => riskApi.skip(patientState?.patientId!!),
    [patientState?.patientId]
  );

  const { mutate: skip } = useMutation(skipQueryFn, {
    onSuccess: () => {
      setPatientState((patientState) =>
        patientState
          ? { ...patientState, isRiskCheckRequired: false }
          : patientState
      );
      queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
    },
  });

  return (
    <Stack direction={"column"} spacing={1}>
      <Stack direction={"row"} spacing={"12px"} alignItems="start">
        <Status status={"human_check_required"} />
        <Stack direction={"column"} sx={{ flex: 1 }}>
          <Typography>{"위험 감지"}</Typography>

          {taskKeyList?.map((taskKey) => (
            <TaskLink
              key={`risk_${taskKey}`}
              taskKey={taskKey}
              thoughtRecordKey={
                taskKey.startsWith("2-2-T") ? thoughtRecordKey : undefined
              }
            />
          ))}
        </Stack>
      </Stack>
      <Stack sx={{ width: "100%" }} direction={"row"} spacing={"4px"}>
        <Button
          size={"xs"}
          variant="Outlined"
          fullWidth
          sx={{ px: "0", flex: 1 }}
          onClick={() => skip()}
        >
          동의서 불필요
        </Button>

        <Button
          size={"xs"}
          variant={"Outlined"}
          disabled={isLoading}
          fullWidth
          sx={{ px: "0", flex: 1 }}
          onClick={() => {
            report();
            setIsConsentModalOpen(true);
          }}
        >
          {"동의서 필요"}
        </Button>
      </Stack>
      <CustomModal
        open={isConsentModalOpen}
        onClose={() => {
          handleAfterReported();
        }}
        title={"<프로그램 범위 점검하기>가 들어갔어요"}
        subtitle={
          "이용자가 <프로그램 범위 점검하기>까지 마치면 업데이트 필요 목록에 올라옵니다."
        }
        actions={[
          {
            label: "확인",
            onClick: () => {
              handleAfterReported();
            },
            variant: "Solid",
          },
        ]}
      />
    </Stack>
  );
}

export default function RiskDetection() {
  const patientState = useRecoilValue(patientAtom);

  const [riskDetail, setRiskDetail] = useState<RiskDetailType>();

  const riskDetailQueryFn = useCallback(
    () => riskApi.getDetail(patientState?.patientId!!),
    [patientState?.patientId]
  );
  const { refetch: getRiskDetail } = useQuery(
    [RISK_DOMAIN, RISK_DETAIL_ENDPOINT, patientState?.patientId],
    riskDetailQueryFn,
    {
      enabled: !!patientState?.patientId,
      onSuccess: (data) => {
        if (data && data.result) {
          const risk = data.result;
          console.log("RISK: ", risk);
          setRiskDetail(risk);
        }
      },
    }
  );

  return riskDetail ? (
    <Stack direction={"column"} spacing={1} sx={{ pb: "8px" }}>
      <Risk
        taskKeyList={riskDetail.taskKeyList}
        thoughtRecordKey={riskDetail.thoughtRecordKey}
        isReportedToMedicalStaff={riskDetail.isReportedToMedicalStaff}
      />
    </Stack>
  ) : (
    <></>
  );
}
